import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TimeTextField from "@/components/forms/TimeTextField";
import { combineLocalDateTimeToIso, splitIsoToLocalDateTime } from "@/lib/timeInput";
import {
    VISIT_TASK_MULTISELECT_TYPES,
    VISIT_TASK_ELIGIBLE_KEYS,
    VISIT_TASK_TITLES,
    VISIT_TYPE_REQUIRES_DESCRIPTION,
    VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT,
    NEW_BOOKING_VISIT_TYPES,
    normaliseAvailableTaskKeys,
    isCanonicalVisitType,
} from "@/lib/visitTaskMapping";

/** APPOINTMENT-TIME-ENTRY · Legacy shim retained so the "onBlackout /
 *  clash resubmit" callers that still pass a datetime-local string
 *  (`YYYY-MM-DDTHH:MM`) keep resolving. The four-control form no
 *  longer produces these values on its own. */
function fromLocalInput(v) {
    if (!v) return null;
    return new Date(v).toISOString();
}

/**
 * CalendarAppointmentForm — unified create/edit form for the Calendar module.
 *
 * Props:
 *   • open                — modal visibility
 *   • initial             — existing appointment (edit mode) or partial (create mode)
 *   • projects            — full project list (installer's world)
 *   • engineers           — full engineer list, active first
 *   • appointmentTypes    — string[] from /api/calendar/appointment-types
 *   • onClose(saved?)     — called after successful save or user cancel
 *
 * Handles:
 *   • create OR edit based on `initial.id`
 *   • project search (customer / postcode / address / reference)
 *   • archived engineers grey and disabled for new assignment (visible on edit
 *     if they were already on the appointment)
 *   • 409 CLASH → surfaces upward via `onClashDetected` so the parent shows
 *     the ClashWarningDialog; on override, form re-submits with clash_override.
 */
export default function CalendarAppointmentForm({
    open,
    initial,
    projects,
    projectsState,           // CAL-001A3 · lifecycle of the parent's project list
    engineers,
    appointmentTypes,
    onClose,
    onClashDetected,
    onAvailabilityWarning,   // OPS-006A · unified availability dialog
    onBlackoutWarning,       // CAL-001 · UK public holidays / manual blackouts
    onOpenAppointment,       // VISIT-TASK-001D · "Open existing appointment" hook
}) {
    const isEdit = Boolean(initial?.id);

    // CAL-001A · When the caller (Project Lead session) couldn't
    // hand us the Ops roster, fall back to the project-scoped
    // assignment-options endpoint once a project is selected. That
    // endpoint is gated to Ops or the Project Lead of that specific
    // project, so this stays server-authoritative — never a client
    // widening.
    //
    // CAL-001A2 · Track the fetch phase distinctly so the empty
    // state, 403 (`ASSIGNMENT_OPTIONS_NOT_AUTHORISED`) and other
    // errors don't all collapse into the same "No active resources"
    // string. `optionsState` values:
    //   idle       — nothing selected yet (or Ops parent supplies).
    //   loading    — request in flight for the current projectId.
    //   ready      — response landed with ≥1 option.
    //   empty      — server returned {options: []} legitimately.
    //   forbidden  — 403 (not the Lead of this project).
    //   error      — network or other unexpected failure.
    const [fallbackEngineers, setFallbackEngineers] = useState([]);
    const [optionsState, setOptionsState] = useState("idle");

    const activeEngineers = useMemo(() => {
        // Ops sessions supply the full roster via the `engineers` prop.
        // Field Project Lead sessions arrive with an empty prop and rely
        // on `fallbackEngineers` fetched from the project-scoped options
        // endpoint after a selection.
        const primary = (engineers || []).filter((e) => e.active !== false);
        if (primary.length) return primary;
        return (fallbackEngineers || []).filter((e) => e.active !== false);
    }, [engineers, fallbackEngineers]);

    const [projectId, setProjectId] = useState("");
    useEffect(() => {
        // CAL-001A2 · Parent-supplied roster short-circuits the
        // whole fallback path. Ops sessions supply the full roster;
        // Lead sessions leave it empty and we fetch the project-
        // scoped options AFTER a selection lands.
        const parentEmpty = !Array.isArray(engineers) || engineers.length === 0;
        if (!open) { setFallbackEngineers([]); setOptionsState("idle"); return; }
        if (!parentEmpty) { setFallbackEngineers([]); setOptionsState("idle"); return; }
        if (!projectId) { setFallbackEngineers([]); setOptionsState("idle"); return; }
        let cancelled = false;
        setOptionsState("loading");
        setFallbackEngineers([]);
        (async () => {
            try {
                const res = await api.projectAssignmentOptions(projectId);
                if (cancelled) return;
                const opts = Array.isArray(res?.options) ? res.options : [];
                setFallbackEngineers(opts);
                setOptionsState(opts.length ? "ready" : "empty");
            } catch (e) {
                if (cancelled) return;
                const status = e?.response?.status;
                setFallbackEngineers([]);
                setOptionsState(status === 403 ? "forbidden" : "error");
            }
        })();
        return () => { cancelled = true; };
    }, [open, engineers, projectId]);
    const [projectSearch, setProjectSearch] = useState("");
    const [showProjectList, setShowProjectList] = useState(false);
    const [type, setType] = useState("Site Survey");
    const [title, setTitle] = useState("");
    // APPOINTMENT-TIME-ENTRY · Four independent controls replace the
    // single `datetime-local` picker. `startTime` / `endTime` are
    // canonical `HH:MM` or empty (only committed on blur — never
    // holds a half-typed "9"). Errors are surfaced inline via the
    // `*TimeErr` states.
    const [startDate, setStartDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endDate, setEndDate] = useState("");
    const [endTime, setEndTime] = useState("");
    const [startTimeErr, setStartTimeErr] = useState("");
    const [endTimeErr, setEndTimeErr] = useState("");
    const [allDay, setAllDay] = useState(false);
    const [engineerIds, setEngineerIds] = useState([]);
    const [status, setStatus] = useState("scheduled");
    const [visibility, setVisibility] = useState("internal_only");
    const [location, setLocation] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    // VISIT-TASK-001D · Structured conflict banner for a
    // ``canonical_task_committed`` refusal. Separate from ``error``
    // so the message and the Open-existing-appointment action have
    // a dedicated render path that never collapses into a plain
    // text banner.
    const [conflict, setConflict] = useState(null);
    const [engineerQuery, setEngineerQuery] = useState("");
    // VISIT-TASK-001 · Visit-task inputs.
    //   * `selectedTaskKeys` — Installation/Commissioning/Handover.
    //   * `scaffoldingVariant` — Scaffolding erect / dismantle.
    //   * `visitTaskDescription` — required for Service / Other.
    //   * `clientRequestId` — one stable UUID per new-booking attempt.
    //     Retained across retries; reset only when opening a NEW
    //     booking (see prefill effect below).
    const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
    const [scaffoldingVariant, setScaffoldingVariant] = useState("");
    const [visitTaskDescription, setVisitTaskDescription] = useState("");
    const [clientRequestId, setClientRequestId] = useState("");
    // Applicable canonical task keys on the current project (fetched
    // via /projects/{id} when Installation/Commissioning/Handover is
    // selected). Restricted to non-complete rows the project actually
    // carries — the backend applies the SAME rules; we mirror them
    // client-side so the UI never offers an unsavable option.
    const [projectDetail, setProjectDetail] = useState(null);
    const [projectDetailLoading, setProjectDetailLoading] = useState(false);
    // CAL-001A1 · Track prior-open state so the reset effect fires
    // exactly once when the caller opens the form (or edits a
    // different appointment). Depending on `initial` / `projects` /
    // `appointmentTypes` here allowed an ancestor SWR refresh to
    // wipe user input mid-typing (Ops portal parent creates fresh
    // inline literals on every render).
    const prevOpenRef = useRef(false);
    const prevInitialIdRef = useRef(null);

    const filteredEngineerChips = useMemo(() => {
        const needle = engineerQuery.trim().toLowerCase();
        if (!needle) return activeEngineers;
        return activeEngineers.filter((e) =>
            (e.displayName || "").toLowerCase().includes(needle) ||
            (e.role || "").toLowerCase().includes(needle)
        );
    }, [activeEngineers, engineerQuery]);

    useEffect(() => {
        if (!open) {
            // Track the closed state so the next open transition
            // triggers a fresh prefill.
            prevOpenRef.current = false;
            prevInitialIdRef.current = null;
            return;
        }
        // CAL-001A1 · Reset ONLY when the form transitions closed→open
        // OR when the caller edits a different appointment (initial.id
        // changed). Re-renders that merely produce new object identities
        // for `initial` / `projects` / `appointmentTypes` (typical of
        // Ops parent + SWR-driven ancestor refreshes) must NOT wipe
        // whatever the user has typed.
        const nowInitialId = initial?.id ?? null;
        const opening = !prevOpenRef.current;
        const changedAppointment = prevOpenRef.current && nowInitialId !== prevInitialIdRef.current;
        prevOpenRef.current = true;
        prevInitialIdRef.current = nowInitialId;
        if (!opening && !changedAppointment) return;
        setError("");
        setProjectId(initial?.project_id || "");
        setType(initial?.appointment_type || appointmentTypes?.[0] || "Site Survey");
        setTitle(initial?.title || "");
        {
            const s = splitIsoToLocalDateTime(initial?.start_at || initial?.start);
            const e = splitIsoToLocalDateTime(initial?.end_at || initial?.end);
            setStartDate(s.date);
            setStartTime(s.time);
            setEndDate(e.date || s.date);
            setEndTime(e.time);
            setStartTimeErr("");
            setEndTimeErr("");
        }
        setAllDay(Boolean(initial?.all_day));
        setEngineerIds(initial?.engineer_ids || []);
        setStatus(initial?.status || "scheduled");
        setVisibility(initial?.visibility || "internal_only");
        setLocation(initial?.location || initial?.project_address || "");
        setNotes(initial?.notes || "");
        // VISIT-TASK-001 · Reset visit-task selections and mint ONE
        // client_request_id for this booking attempt. Retries reuse
        // it; only a fresh open (or edit target change) mints a new
        // one. In edit mode the id is irrelevant server-side.
        setSelectedTaskKeys([]);
        setScaffoldingVariant("");
        setVisitTaskDescription("");
        setClientRequestId(() => {
            if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
                return crypto.randomUUID();
            }
            return `vt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        });
        // Pre-populate project search text with the plain customer
        // name so the search filter still finds the pre-selected
        // project when the user opens the dropdown. Previously used
        // an em-dash separator that made `filteredProjects` return
        // "No matching projects" even for the exact pre-selected id.
        //
        // DEV-019/020 UAT correction · In edit mode a Sales caller
        // typically has NO projects roster (the schedule-visit UI
        // only loads a roster for Ops / Project Lead sessions), so
        // the roster-based label lookup returns empty and the locked
        // Project field appeared blank even though the appointment
        // was correctly linked. Fall back to the customer name +
        // address echoed on the appointment itself so the field
        // always identifies the actual project. Project remains
        // strictly locked (`disabled={isEdit}`) — this only affects
        // the display label.
        const p = (projects || []).find((x) => x.id === (initial?.project_id || ""));
        let derivedLabel = "";
        if (p) {
            derivedLabel = (p.customer_name || "").trim();
        } else if (initial?.id && initial?.project_id) {
            const parts = [
                (initial.customer_name || "").trim(),
                (initial.project_address || initial.address || "").trim(),
            ].filter(Boolean);
            derivedLabel = parts.join(" — ");
        }
        setProjectSearch(derivedLabel);
    }, [open, initial, projects, appointmentTypes]);

    // CAL-001A1 · When the projects dataset arrives AFTER the initial
    // prefill (e.g. lazy load in the Ops portal), rehydrate the
    // project search text once the pre-selected id becomes findable.
    // We never clear `projectId` here — canonical id survives async
    // dataset arrival even when the label is not yet resolvable.
    //
    // DEV-019/020 UAT correction · Skip the roster-based rehydrate
    // when we're editing an existing appointment AND already have a
    // non-empty label sourced from the appointment itself; otherwise
    // a late roster arrival would replace the appointment-derived
    // "Customer — Address" fallback with a bare customer name.
    useEffect(() => {
        if (!open || !projectId || projectSearch) return;
        const p = (projects || []).find((x) => x.id === projectId);
        if (p) setProjectSearch((p.customer_name || "").trim());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projects, projectId]);

    // Auto-set location when project changes if user hasn't edited it
    useEffect(() => {
        const p = (projects || []).find((x) => x.id === projectId);
        if (p && !location.trim()) setLocation(p.address || "");
    }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

    // VISIT-TASK-001 · Fetch project detail (phase tasks) whenever
    // the user selects a project + a multi-select visit type. The
    // backend endpoint is already scoped to the authorised user;
    // failure is soft — the user then sees "no applicable tasks"
    // rather than a crash.
    useEffect(() => {
        if (!open) return;
        if (!projectId) { setProjectDetail(null); return; }
        if (!VISIT_TASK_MULTISELECT_TYPES.has(type)) {
            setProjectDetail(null);
            return;
        }
        let cancelled = false;
        setProjectDetailLoading(true);
        (async () => {
            try {
                const p = await api.getProject(projectId);
                if (!cancelled) setProjectDetail(p || null);
            } catch {
                if (!cancelled) setProjectDetail(null);
            } finally {
                if (!cancelled) setProjectDetailLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, projectId, type]);

    // Applicable canonical task_keys under the current visit-type.
    const applicableTaskKeys = useMemo(
        () => normaliseAvailableTaskKeys(projectDetail, type),
        [projectDetail, type],
    );

    // Reset stale selections when the visit type changes.
    useEffect(() => {
        setSelectedTaskKeys((prev) => prev.filter((k) => (VISIT_TASK_ELIGIBLE_KEYS[type] || []).includes(k)));
        if (!VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT.has(type)) setScaffoldingVariant("");
        if (!VISIT_TYPE_REQUIRES_DESCRIPTION.has(type)) setVisitTaskDescription("");
    }, [type]);
    const filteredProjects = useMemo(() => {
        const needle = projectSearch.trim().toLowerCase();
        if (!needle) return (projects || []).slice(0, 20);
        return (projects || []).filter((p) => {
            const hay = [p.customer_name, p.address, p.system_summary, p.id]
                .map((x) => String(x || "").toLowerCase()).join(" ");
            return hay.includes(needle);
        }).slice(0, 30);
    }, [projects, projectSearch]);

    // APPOINTMENT-TIME-ENTRY · Compose canonical ISO from the four
    // controls. Returns null while any control is empty / invalid so
    // the submit button stays disabled and the missing-field banner
    // can explain why.
    const startIso = useMemo(
        () => combineLocalDateTimeToIso(startDate, startTime),
        [startDate, startTime],
    );
    const endIso = useMemo(
        () => combineLocalDateTimeToIso(endDate, endTime),
        [endDate, endTime],
    );

    const canSave = projectId
        && startDate && endDate && startTime && endTime
        && !startTimeErr && !endTimeErr
        && startIso && endIso
        && type
        && (engineerIds.length > 0 || allDay)
        && new Date(endIso) > new Date(startIso)
        && (
            // VISIT-TASK-001 · Client-side pre-check mirrors the
            // backend resolver so the Save button is disabled with
            // a specific missing-field reason rather than a
            // post-submit 422.
            (isEdit) ||
            (!VISIT_TASK_MULTISELECT_TYPES.has(type) || selectedTaskKeys.length > 0)
        )
        && (isEdit || !VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT.has(type) || !!scaffoldingVariant)
        && (isEdit || !VISIT_TYPE_REQUIRES_DESCRIPTION.has(type) || visitTaskDescription.trim().length > 0);

    // CAL-001A1 · Human-readable diagnosis of every missing/invalid
    // field so a disabled Create button always carries a visible
    // reason. Ordered to match form field order for at-a-glance
    // troubleshooting.
    const missingReasons = useMemo(() => {
        const reasons = [];
        if (!projectId) reasons.push("Select a project");
        if (!type) reasons.push("Choose an appointment type");
        if (!startDate) reasons.push("Enter a start date");
        if (!startTime || startTimeErr) reasons.push("Enter a start time (HH:MM)");
        if (!endDate) reasons.push("Enter an end date");
        if (!endTime || endTimeErr) reasons.push("Enter an end time (HH:MM)");
        if (startIso && endIso && new Date(endIso) <= new Date(startIso)) {
            reasons.push("End must be after start");
        }
        if (!allDay && engineerIds.length === 0) {
            reasons.push("Assign at least one resource");
        }
        if (!isEdit && VISIT_TASK_MULTISELECT_TYPES.has(type) && selectedTaskKeys.length === 0) {
            reasons.push(`Select at least one applicable ${type.toLowerCase()} task`);
        }
        if (!isEdit && VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT.has(type) && !scaffoldingVariant) {
            reasons.push("Choose scaffolding: erect or dismantle");
        }
        if (!isEdit && VISIT_TYPE_REQUIRES_DESCRIPTION.has(type) && !visitTaskDescription.trim()) {
            reasons.push(`Describe the ${type.toLowerCase()} work`);
        }
        return reasons;
    }, [projectId, type, startDate, startTime, endDate, endTime,
        startTimeErr, endTimeErr, startIso, endIso, allDay, engineerIds,
        isEdit, selectedTaskKeys, scaffoldingVariant, visitTaskDescription]);

    async function submit(clashOverride = false, availabilityOverride = false, skipPreCheck = false, blackoutOverride = false) {
        if (!canSave) { setError("Please complete all required fields."); return; }
        if (!startIso || !endIso) { setError("Please complete all required fields."); return; }
        if (new Date(endIso) <= new Date(startIso)) {
            setError("End must be after start.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const startIsoLocal = startIso;
            const endIsoLocal = endIso;

            // OPS-006A1 · Consolidated pre-submit check is the SINGLE
            // warning source. It already includes appointment clashes,
            // holidays and non-working-day rows, so we never surface
            // the legacy clash dialog + availability dialog in
            // sequence. The backend still enforces both codes as
            // defense-in-depth (see the 409 handler below).
            const runPreCheck =
                !skipPreCheck
                && onAvailabilityWarning
                && engineerIds.length > 0
                && !clashOverride
                && !availabilityOverride;
            if (runPreCheck) {
                try {
                    const check = await api.checkCalendarAvailability({
                        engineer_ids: engineerIds,
                        start_at: startIsoLocal,
                        end_at: endIsoLocal,
                        ignore_appointment_id: isEdit ? initial.id : null,
                    });
                    if (check?.has_warning) {
                        setSaving(false);
                        onAvailabilityWarning({
                            warnings: check.warnings || [],
                            resubmit: () => submit(true, true, true),
                        });
                        return;
                    }
                } catch {
                    // Fall through to the write — the backend 409 path
                    // still guards the invariant if the pre-check
                    // fails for any reason.
                }
            }

            const body = {
                project_id: projectId,
                appointment_type: type,
                title: title || type,
                start_at: startIsoLocal,
                end_at: endIsoLocal,
                all_day: allDay,
                engineer_ids: engineerIds,
                status,
                visibility,
                location: location || null,
                notes: notes || null,
                clash_override: clashOverride,
                availability_override: availabilityOverride,
                blackout_override: blackoutOverride,
                // VISIT-TASK-001 · New-booking task linkage inputs.
                // `client_request_id` is the same UUID across every
                // retry inside this booking attempt so the backend
                // short-circuits the idempotency check and never
                // creates duplicate appointments or tasks.
                ...(isEdit ? {} : { client_request_id: clientRequestId }),
                ...(VISIT_TASK_MULTISELECT_TYPES.has(type) && !isEdit
                    ? { required_task_selections: selectedTaskKeys }
                    : {}),
                ...(VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT.has(type) && !isEdit
                    ? { scaffolding_variant: scaffoldingVariant }
                    : {}),
                ...(VISIT_TYPE_REQUIRES_DESCRIPTION.has(type) && !isEdit
                    ? { visit_task_description: visitTaskDescription.trim() }
                    : {}),
            };
            const saved = isEdit
                ? await api.updateCalendarAppointment(initial.id, body)
                : await api.createCalendarAppointment(body);
            // VISIT-TASK-001D · Toast now offers a "View" action so
            // the user can jump directly to the saved appointment
            // (via the parent's onOpenAppointment hook when
            // supplied). Falls back to a plain success toast when
            // no hook is wired.
            if (!isEdit && saved?.id && onOpenAppointment) {
                toast.success("Appointment created", {
                    action: {
                        label: "View",
                        onClick: () => onOpenAppointment(saved.id),
                    },
                });
            } else {
                toast.success(isEdit ? "Appointment updated" : "Appointment created");
            }
            onClose?.(saved);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // CAL-001 · UK bank-holiday / manual blackout confirmation
            // is enforced server-side. Surface upward — the parent
            // renders BlackoutConfirmationDialog and re-submits with
            // `blackout_override=true` on confirm. Go back writes
            // nothing.
            if (detail?.code === "BLACKOUT_DATE_CONFIRMATION_REQUIRED" && onBlackoutWarning) {
                setSaving(false);
                onBlackoutWarning({
                    matches: detail.matches || [],
                    date: detail.primary_date,
                    resubmit: () => submit(clashOverride, availabilityOverride, true, true),
                });
                return;
            }
            // OPS-006A1 · Route BOTH backend warning codes through the
            // single consolidated dialog. The legacy `onClashDetected`
            // callback stays wired for other callers, but is no longer
            // the primary handler when the availability route exists.
            if ((detail?.code === "CLASH" || detail?.code === "AVAILABILITY_WARNING") && onAvailabilityWarning) {
                try {
                    const check = await api.checkCalendarAvailability({
                        engineer_ids: engineerIds,
                        start_at: startIso,
                        end_at: endIso,
                        ignore_appointment_id: isEdit ? initial.id : null,
                    });
                    onAvailabilityWarning({
                        warnings: check?.warnings || [],
                        resubmit: () => submit(true, true, true),
                    });
                } catch {
                    setError(formatApiError(detail) || "Failed to save appointment");
                }
            } else if (detail?.code === "CLASH" && onClashDetected) {
                // Legacy fallback for callers not wired to
                // onAvailabilityWarning (kept intentionally — see
                // OPS-006A1 brief).
                onClashDetected({
                    engineer_ids: engineerIds,
                    start_at: startIso,
                    end_at: endIso,
                    ignore_appointment_id: isEdit ? initial.id : null,
                    resubmit: () => submit(true, availabilityOverride),
                });
            } else if (detail?.code === "canonical_task_committed") {
                // VISIT-TASK-001D · The chosen visit type maps to a
                // canonical task that is already linked to another
                // active appointment. Surface a dedicated conflict
                // banner so the user can jump straight to the
                // existing appointment instead of re-typing the
                // form. Entered values are preserved (we only set
                // conflict state — no other mutation).
                setConflict({
                    message: detail.detail || "This task is already linked to another appointment.",
                    appointmentId: detail.conflicting_appointment_id,
                    appointmentType: detail.conflicting_appointment_type,
                    startAt: detail.conflicting_appointment_start_at,
                    endAt: detail.conflicting_appointment_end_at,
                    status: detail.conflicting_appointment_status,
                    title: detail.conflicting_appointment_title,
                });
            } else {
                setError(formatApiError(detail) || "Failed to save appointment");
            }
        } finally {
            setSaving(false);
        }
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/55 flex items-end md:items-center justify-center md:px-4"
            data-testid="cal-form-modal"
        >
            <div className="bg-white w-full md:max-w-2xl md:rounded-2xl md:shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="px-5 md:px-6 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-[#131B2F] flex-1">
                        {isEdit ? "Edit appointment" : "New appointment"}
                    </h3>
                    <button
                        onClick={() => onClose?.()}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] text-[#4B5565]"
                        aria-label="Close"
                        data-testid="cal-form-close"
                    >
                        <X size={18} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="px-5 md:px-6 py-4 overflow-y-auto space-y-4 flex-1">
                    {/* Project */}
                    <div>
                        <label className="text-sm font-medium text-[#131B2F] block mb-1">Project *</label>
                        <div className="relative">
                            <div className="flex items-center gap-2 px-3 py-2.5 border border-[#D1D5DB] rounded-md bg-white">
                                <Search size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                                <input
                                    type="text"
                                    value={projectSearch}
                                    onChange={(e) => { setProjectSearch(e.target.value); setShowProjectList(true); }}
                                    onFocus={() => setShowProjectList(true)}
                                    placeholder="Search by customer, postcode, address…"
                                    data-testid="cal-form-project-search"
                                    className="flex-1 min-w-0 outline-none text-sm bg-transparent"
                                    disabled={isEdit}
                                />
                            </div>
                            {showProjectList && !isEdit && (
                                <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-[#E5E7EB] rounded-md shadow-lg" data-testid="cal-form-project-list">
                                    {projectsState === "loading" && (
                                        <div className="px-3 py-2 text-sm text-[#4B5565]" data-testid="cal-form-projects-loading">Loading your projects…</div>
                                    )}
                                    {projectsState === "empty" && filteredProjects.length === 0 && (
                                        <div className="px-3 py-2 text-sm text-[#4B5565]" data-testid="cal-form-projects-empty">You don't lead any projects yet.</div>
                                    )}
                                    {projectsState === "forbidden" && (
                                        <div className="px-3 py-2 text-sm text-[#4B5565]" data-testid="cal-form-projects-forbidden">You don't have access to any projects here.</div>
                                    )}
                                    {projectsState === "error" && (
                                        <div className="px-3 py-2 text-sm text-[#4B5565]" data-testid="cal-form-projects-error">Couldn't load your projects. Please retry.</div>
                                    )}
                                    {(!projectsState || projectsState === "ready") && filteredProjects.length === 0 && (
                                        <div className="px-3 py-2 text-sm text-[#4B5565]">No matching projects</div>
                                    )}
                                    {filteredProjects.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => {
                                                // CAL-001A2 · Changing project
                                                // clears stale selected resources
                                                // AND the project-derived location
                                                // so the auto-populate effect below
                                                // can lay down the new project's
                                                // address on the next tick.
                                                if (p.id !== projectId) {
                                                    setEngineerIds([]);
                                                    setFallbackEngineers([]);
                                                    setOptionsState("idle");
                                                    setLocation("");
                                                }
                                                setProjectId(p.id);
                                                setProjectSearch(`${p.customer_name} — ${p.address || ""}`.trim());
                                                setShowProjectList(false);
                                            }}
                                            data-testid={`cal-form-project-option-${p.id}`}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F7] border-b border-[#F3F4F6] last:border-b-0"
                                        >
                                            <div className="font-medium text-[#131B2F]">{p.customer_name}</div>
                                            <div className="text-xs text-[#4B5565] truncate">{p.address}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {projectId && !isEdit && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        // CAL-001A2 · Clear/change project
                                        // without closing the modal — clears
                                        // every project-derived field so a
                                        // subsequent selection can repopulate
                                        // Location from the new address.
                                        setProjectId("");
                                        setProjectSearch("");
                                        setEngineerIds([]);
                                        setFallbackEngineers([]);
                                        setOptionsState("idle");
                                        setLocation("");
                                        setShowProjectList(true);
                                    }}
                                    data-testid="cal-form-project-clear"
                                    className="mt-1 text-xs text-[#4B5565] underline hover:text-[#131B2F]"
                                >
                                    Change project
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Type + Title */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Appointment type *</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                disabled={isEdit}
                                aria-disabled={isEdit}
                                className={`w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white ${isEdit ? "cursor-not-allowed opacity-70" : ""}`}
                                data-testid="cal-form-type"
                            >
                                {/* VISIT-TASK-001 · Show only new-booking
                                    types in create mode. In edit mode we
                                    surface whatever the row currently
                                    carries (including legacy Technical
                                    Survey / Maintenance rows) so history
                                    is honoured. */}
                                {(isEdit ? appointmentTypes : NEW_BOOKING_VISIT_TYPES).map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            {isEdit && (
                                <p
                                    className="text-xs text-[#4B5565] mt-1"
                                    data-testid="cal-form-type-locked-explanation"
                                >
                                    Visit type is locked after booking to preserve required-task links. Cancel this appointment and create a new one to change the visit type.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={type}
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                                data-testid="cal-form-title"
                            />
                        </div>
                    </div>

                    {/* VISIT-TASK-001 · Visit-task selection controls.
                        Rendered ONLY in create mode; edit mode leaves
                        the existing links untouched (backend does not
                        currently reconcile links on visit-type change
                        via PATCH — see known limitations). */}
                    {!isEdit && VISIT_TASK_MULTISELECT_TYPES.has(type) && (
                        <div data-testid="cal-form-visit-task-multiselect">
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">
                                Applicable {type.toLowerCase()} tasks *
                            </label>
                            <p className="text-xs text-[#4B5565] mb-2">
                                Select the work this visit will complete. Only tasks that exist on this project and are not already complete are listed.
                            </p>
                            {projectDetailLoading && (
                                <div className="text-xs text-[#4B5565]" data-testid="cal-form-visit-task-loading">Loading project tasks…</div>
                            )}
                            {!projectDetailLoading && applicableTaskKeys.length === 0 && (
                                <div className="text-xs text-[#4B5565] bg-[#FFF8EB] border border-[#F5A524]/40 rounded-md px-3 py-2" data-testid="cal-form-visit-task-empty">
                                    No applicable {type.toLowerCase()} tasks are available on this project. Confirm the project stage before booking this visit.
                                </div>
                            )}
                            {applicableTaskKeys.length > 0 && (
                                <div className="flex flex-col gap-1.5" data-testid="cal-form-visit-task-options">
                                    {applicableTaskKeys.map((k) => {
                                        const checked = selectedTaskKeys.includes(k);
                                        return (
                                            <label key={k} className="inline-flex items-center gap-2 text-sm text-[#131B2F]">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() =>
                                                        setSelectedTaskKeys((prev) =>
                                                            prev.includes(k)
                                                                ? prev.filter((x) => x !== k)
                                                                : [...prev, k],
                                                        )
                                                    }
                                                    data-testid={`cal-form-visit-task-${k}`}
                                                />
                                                {VISIT_TASK_TITLES[k] || k}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {!isEdit && VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT.has(type) && (
                        <div data-testid="cal-form-visit-task-scaffolding">
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">
                                Scaffolding visit *
                            </label>
                            <div className="flex gap-2">
                                {[
                                    { v: "erect", label: "Erection" },
                                    { v: "dismantle", label: "Removal" },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setScaffoldingVariant(opt.v)}
                                        aria-pressed={scaffoldingVariant === opt.v}
                                        data-testid={`cal-form-scaffolding-${opt.v}`}
                                        className={`px-3 py-2 rounded-md border text-sm ${
                                            scaffoldingVariant === opt.v
                                                ? "bg-[#131B2F] text-white border-[#131B2F]"
                                                : "bg-white text-[#131B2F] border-[#D1D5DB] hover:bg-[#F5F5F7]"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-[#4B5565] mt-1">
                                Erection visits complete separately from removal — no visit ever waits on the other.
                            </p>
                        </div>
                    )}

                    {!isEdit && VISIT_TYPE_REQUIRES_DESCRIPTION.has(type) && (
                        <div data-testid="cal-form-visit-task-description-wrap">
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">
                                Describe the {type.toLowerCase()} work *
                            </label>
                            <textarea
                                value={visitTaskDescription}
                                onChange={(e) => setVisitTaskDescription(e.target.value)}
                                rows={2}
                                data-testid="cal-form-visit-task-description"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white resize-y"
                                placeholder={type === "Service" ? "e.g. Annual system health check" : "Describe the work covered by this visit"}
                            />
                        </div>
                    )}

                    {/* Times */}
                    {/* APPOINTMENT-TIME-ENTRY · Four independent
                        controls — start date, start time, end date,
                        end time. Time fields are plain text (NEVER
                        `type="time"` / `datetime-local`) so users
                        can type "9", "9:00" or "0900" and see it
                        normalise to "09:00" on blur. Cross-day
                        appointments are supported by allowing end
                        date to differ from start date. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cal-form-times">
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Start date *</label>
                            <input
                                type="date"
                                value={startDate}
                                // VISIT-TASK-001B · Native date pickers on
                                // some browsers commit the value via `input`
                                // rather than `change`; keyboard-driven
                                // entry only commits on blur. Wire every
                                // authoritative event so React state and
                                // the missing-fields banner update the
                                // moment the field carries a real value.
                                onChange={(e) => setStartDate(e.target.value)}
                                onInput={(e) => setStartDate(e.currentTarget.value)}
                                onBlur={(e) => setStartDate(e.currentTarget.value)}
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                                data-testid="cal-form-start-date"
                                aria-label="Start date"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Start time *</label>
                            <TimeTextField
                                value={startTime}
                                onChange={(canonical, err) => {
                                    setStartTime(canonical);
                                    setStartTimeErr(err || "");
                                }}
                                error={startTimeErr}
                                ariaLabel="Start time"
                                testid="cal-form-start-time"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                            />
                            {startTimeErr && (
                                <div className="mt-1 text-xs text-[#DA3535]" data-testid="cal-form-start-time-error">
                                    {startTimeErr}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">End date *</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                onInput={(e) => setEndDate(e.currentTarget.value)}
                                onBlur={(e) => setEndDate(e.currentTarget.value)}
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                                data-testid="cal-form-end-date"
                                aria-label="End date"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">End time *</label>
                            <TimeTextField
                                value={endTime}
                                onChange={(canonical, err) => {
                                    setEndTime(canonical);
                                    setEndTimeErr(err || "");
                                }}
                                error={endTimeErr}
                                ariaLabel="End time"
                                testid="cal-form-end-time"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                            />
                            {endTimeErr && (
                                <div className="mt-1 text-xs text-[#DA3535]" data-testid="cal-form-end-time-error">
                                    {endTimeErr}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assigned resources */}
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <label className="text-sm font-medium text-[#131B2F]">Assigned resources</label>
                            <span className="text-xs text-[#4B5565]" data-testid="cal-form-assigned-count">
                                {engineerIds.length ? `${engineerIds.length} selected` : "Assign at least one resource"}                            </span>
                        </div>
                        {activeEngineers.length > 8 && (
                            <input
                                type="text"
                                value={engineerQuery}
                                onChange={(e) => setEngineerQuery(e.target.value)}
                                placeholder="Filter resources…"
                                data-testid="cal-form-engineer-search"
                                className="w-full mb-2 px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white"
                            />
                        )}
                        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="cal-form-engineers">
                            {filteredEngineerChips.map((e) => {
                                const selected = engineerIds.includes(e.id);
                                return (
                                    <button
                                        key={e.id}
                                        type="button"
                                        onClick={() =>
                                            setEngineerIds((prev) =>
                                                prev.includes(e.id) ? prev.filter((x) => x !== e.id) : [...prev, e.id]
                                            )
                                        }
                                        data-testid={`cal-form-engineer-${e.id}`}
                                        aria-pressed={selected}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                            selected
                                                ? "text-white border-transparent"
                                                : "text-[#131B2F] border-[#D1D5DB] hover:bg-[#F5F5F7] bg-white"
                                        }`}
                                        style={selected ? { background: e.colour || "#3E9E2E" } : {}}
                                    >
                                        <span
                                            className="w-2.5 h-2.5 rounded-full inline-block"
                                            style={{ background: selected ? "#ffffff" : (e.colour || "#3E9E2E") }}
                                        />
                                        {e.displayName}
                                    </button>
                                );
                            })}
                            {activeEngineers.length === 0 && (
                                <span className="text-xs text-[#4B5565]" data-testid="cal-form-engineers-empty">
                                    {(() => {
                                        if (!projectId) return "Select a project to load assignment options.";
                                        if (optionsState === "loading") return "Loading assignment options…";
                                        if (optionsState === "forbidden") return "You are not the Project Lead for this project.";
                                        if (optionsState === "error") return "Couldn't load assignment options. Please retry.";
                                        return "No active resources for this project.";
                                    })()}
                                </span>
                            )}
                            {activeEngineers.length > 0 && filteredEngineerChips.length === 0 && (
                                <span className="text-xs text-[#4B5565]">No resources match "{engineerQuery}".</span>
                            )}
                        </div>
                    </div>

                    {/* Location + Status/Visibility */}
                    <div>
                        <label className="text-sm font-medium text-[#131B2F] block mb-1">Location</label>
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                            data-testid="cal-form-location"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                                data-testid="cal-form-status"
                            >
                                <option value="scheduled">Scheduled</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="in_progress">In progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Visibility</label>
                            <select
                                value={visibility}
                                onChange={(e) => setVisibility(e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white"
                                data-testid="cal-form-visibility"
                            >
                                <option value="internal_only">Internal only</option>
                                <option value="customer_visible">Customer visible</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-[#131B2F] block mb-1">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white resize-y"
                            data-testid="cal-form-notes"
                        />
                    </div>

                    {conflict && (
                        <div
                            className="text-sm text-[#131B2F] bg-[#FEF3C7] border border-[#F5C86A] rounded-md p-3 space-y-2"
                            data-testid="cal-form-conflict-banner"
                            role="alert"
                        >
                            <div className="font-medium">This task is already linked to another appointment</div>
                            <div className="text-xs text-[#4B5565]" data-testid="cal-form-conflict-message">{conflict.message}</div>
                            {(conflict.startAt || conflict.appointmentType) && (
                                <div className="text-xs text-[#4B5565]" data-testid="cal-form-conflict-details">
                                    {conflict.title || conflict.appointmentType || "Appointment"}
                                    {conflict.startAt && (
                                        <> · {new Date(conflict.startAt).toLocaleString(undefined, {
                                            weekday: "short", day: "numeric", month: "short",
                                            year: "numeric", hour: "2-digit", minute: "2-digit",
                                        })}</>
                                    )}
                                    {conflict.status && <> · {conflict.status}</>}
                                </div>
                            )}
                            <div className="flex gap-2 pt-1">
                                {conflict.appointmentId && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (onOpenAppointment) {
                                                onOpenAppointment(conflict.appointmentId);
                                            }
                                            setConflict(null);
                                            onClose?.();
                                        }}
                                        className="h-8 px-3 rounded-md bg-[#3E9E2E] text-white text-xs font-medium hover:opacity-90"
                                        data-testid="cal-form-conflict-open-existing"
                                    >
                                        Open existing appointment
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setConflict(null)}
                                    className="h-8 px-3 rounded-md border border-[#E5E7EB] text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                                    data-testid="cal-form-conflict-dismiss"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}
                    {error && <div className="text-sm text-[#DA3535]" data-testid="cal-form-error">{error}</div>}
                    {!error && !canSave && missingReasons.length > 0 && (
                        <div
                            data-testid="cal-form-missing-fields"
                            className="text-xs text-[#4B5565] bg-[#FFF8EB] border border-[#F5A524]/40 rounded-md px-3 py-2"
                        >
                            <div className="font-medium text-[#7A5B00] mb-1">To create this appointment:</div>
                            <ul className="list-disc pl-4 space-y-0.5">
                                {missingReasons.map((r) => (
                                    <li key={r} data-testid={`cal-form-missing-${r.replace(/\s+/g,'-').toLowerCase().slice(0,40)}`}>{r}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="px-5 md:px-6 py-4 border-t border-[#E5E7EB] flex flex-wrap justify-end gap-2">
                    <button
                        onClick={() => onClose?.()}
                        className="px-4 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium hover:bg-[#F5F5F7]"
                        data-testid="cal-form-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => submit(false)}
                        disabled={!canSave || saving}
                        className="px-4 py-2 rounded-md bg-[#3E9E2E] text-white text-sm font-medium hover:bg-[#348426] disabled:opacity-50"
                        data-testid="cal-form-submit"
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Create appointment"}
                    </button>
                </div>
            </div>
        </div>
    );
}
